import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { formatTextContent } from '../../services/funnelService';

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: ${theme.spacing.screenPadding};
  padding-top: calc(${theme.spacing.screenPadding} + 24px);
  padding-bottom: calc(${theme.spacing.screenPadding} + 120px); /* Extra bottom padding for mobile CTA access */
  overflow-y: auto;
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding-bottom: calc(${theme.spacing.screenPadding} + 200px); /* Even more bottom padding on mobile for multi-line buttons */
  }
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  /* Remove justify-content: center to prevent content from being pushed down */
  text-align: center;
  gap: ${theme.spacing.lg};
  min-height: 0; /* Allow content to shrink */
`;

const Title = styled.h2`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.lg};
`;

const Description = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  line-height: ${theme.typography.lineHeights.normal};
  margin-bottom: 8px;
`;


const ContinueButton = styled.button`
  background: ${theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.buttonPadding};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.touch};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  min-height: 48px;
  box-shadow: ${theme.shadows.sm};
  /* Button is now in content flow, adjust spacing */
  margin-top: 0;
  margin-bottom: 24px;
  /* Better width handling for longer text */
  min-width: 200px;
  width: fit-content;
  max-width: 90%;
  align-self: center;
  /* Multi-line text support for very long text */
  white-space: normal;
  line-height: 1.2;
  text-align: center;
  /* Ensure proper touch targets on mobile */
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: ${theme.shadows.sm};
  }

  &:disabled {
    background: ${theme.colors.disabled};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: 56px; /* Larger touch target on mobile */
    padding: 1rem 1.5rem; /* More padding for better touch experience */
  }
  
  /* For very small screens, ensure text fits */
  @media (max-width: 320px) {
    font-size: 0.875rem;
    padding: 0.75rem 1rem;
    min-width: 150px;
  }
`;

const NoDataMessage = styled.div`
  background: ${theme.colors.neutral}20;
  border: 2px solid ${theme.colors.neutral}40;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  margin: ${theme.spacing.lg} 0;
  width: 100%;
  max-width: 400px;
  text-align: center;
`;

const NoDataTitle = styled.h3`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.sm};
`;

const NoDataText = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  line-height: ${theme.typography.lineHeights.normal};
`;

// Visualization Components (matching Safe Sprinting style)
const VisualizationContainer = styled.div`
  width: 100%;
  max-width: 80vw;
  margin: ${theme.spacing.xs} auto 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const VisualizationTitle = styled.h4`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.md};
  letter-spacing: ${theme.typography.letterSpacing.tight};
`;

const ChartContainer = styled.div`
  width: 80vw;
  max-width: 80vw;
  height: 182px;
  position: relative;
  overflow: visible;
  margin: ${theme.spacing.md} auto 0 auto;
  padding: 8px 10px 40px 10px;
  box-sizing: border-box;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 80vw;
    max-width: 80vw;
  }
`;

const ChartArea = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  padding: 20px 0 10px 0;
`;

const XAxis = styled.div`
  position: absolute;
  bottom: 20px;
  height: 2px;
  background: ${theme.colors.doryLighterBlue};
  z-index: 1;
`;

const YAxis = styled.div`
  position: absolute;
  top: 0;
  bottom: 20px;
  width: 2px;
  background: ${theme.colors.doryLighterBlue};
  z-index: 1;
`;

const DataPoint = styled.div`
  position: absolute;
  width: 8px;
  height: 8px;
  background: ${theme.colors.primary};
  border: 2px solid white;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  z-index: 3;
  box-shadow: ${theme.shadows.sm};
  cursor: pointer;
  
  &:hover {
    transform: translate(-50%, -50%) scale(1.2);
    z-index: 5;
  }
`;

const Tooltip = styled.div`
  position: absolute;
  background: ${theme.colors.text};
  color: white;
  padding: 6px 8px;
  border-radius: ${theme.borderRadius.sm};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  font-weight: ${theme.typography.weights.medium};
  white-space: nowrap;
  z-index: 6;
  pointer-events: none;
  box-shadow: ${theme.shadows.md};
  
  /* Position tooltip above the dot */
  transform: translate(-50%, -100%);
  margin-top: -8px;
  
  /* Arrow pointing down */
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: ${theme.colors.text};
  }
`;

const BestFitLine = styled.div`
  position: absolute;
  height: 4px;
  background: ${theme.colors.doryDarkerBlue};
  z-index: 2;
  transform-origin: left center;
`;

const SlopeLabel = styled.div`
  position: absolute;
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.accent};
  background: ${theme.colors.surface};
  padding: 2px 6px;
  border-radius: ${theme.borderRadius.sm};
  border: 1px solid ${theme.colors.accent};
  z-index: 4;
  transform: translate(-50%, -50%);
`;

const XAxisLabel = styled.div`
  position: absolute;
  bottom: -25px;
  left: 50%;
  transform: translateX(-50%);
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  font-weight: ${theme.typography.weights.medium};
`;

const YAxisLabel = styled.div`
  position: absolute;
  left: -60px;
  top: 50%;
  transform: translateY(-50%) rotate(-90deg);
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  font-weight: ${theme.typography.weights.medium};
  white-space: nowrap;
`;

const GridLine = styled.div`
  position: absolute;
  width: 1px;
  background: ${theme.colors.doryLighterBlue}40;
  z-index: 1;
`;

// Bar Visualization Components
const BarContainer = styled.div`
  width: 100%;
  max-width: 80vw;
  margin: ${theme.spacing.md} auto ${theme.spacing.lg} auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const BarWrapper = styled.div`
  width: 80vw;
  max-width: 80vw;
  height: 40px;
  position: relative;
  background: ${theme.colors.doryBeige};
  border-radius: ${theme.borderRadius.lg};
  overflow: visible;
  box-shadow: ${theme.shadows.sm};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 80vw;
    max-width: 80vw;
  }
`;

const BarSegment = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  height: 100%;
  transition: all ${theme.animation.durations.normal} ${theme.animation.easings.easeOut};
`;

const ScoreIndicator = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 12px;
  height: 12px;
  background: ${theme.colors.text};
  border: 3px solid white;
  border-radius: 50%;
  z-index: 10;
  box-shadow: ${theme.shadows.md};
  transition: all ${theme.animation.durations.normal} ${theme.animation.easings.easeOut};
`;

const ScoreLabel = styled.div`
  position: absolute;
  top: -43px;
  left: 50%;
  transform: translateX(-50%);
  background: ${theme.colors.text};
  color: white;
  padding: 6px 12px;
  border-radius: ${theme.borderRadius.sm};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.semiBold};
  white-space: nowrap;
  z-index: 11;
  box-shadow: ${theme.shadows.sm};
  
  /* Arrow pointing down */
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: ${theme.colors.text};
  }
`;

const BarLabels = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  display: flex;
  justify-content: space-between;
  padding: 0 12px;
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  font-weight: ${theme.typography.weights.medium};
  pointer-events: none;
  z-index: 5;
`;

const BarLabel = styled.span`
  text-align: center;
  min-width: 20px;
`;







const MentalResilienceScreen = ({ data, onNext, userProgress = {} }) => {
  const [mentalResilienceData, setMentalResilienceData] = useState(userProgress.calculatedMetrics?.mentalResilience);
  const [chartWidth, setChartWidth] = useState(320); // Default width
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const chartRef = useRef(null);
  
  // Update mental resilience data when userProgress changes
  useEffect(() => {
    const newMentalResilienceData = userProgress.calculatedMetrics?.mentalResilience;
    if (newMentalResilienceData) {
      console.log('MentalResilienceScreen: Received mental resilience data', newMentalResilienceData);
      setMentalResilienceData(newMentalResilienceData);
    }
  }, [userProgress]);

  // Update chart width when component mounts or resizes
  useEffect(() => {
    const updateChartWidth = () => {
      if (chartRef.current) {
        setChartWidth(chartRef.current.offsetWidth);
      }
    };

    // Update on mount
    updateChartWidth();

    // Update on resize
    window.addEventListener('resize', updateChartWidth);
    
    return () => window.removeEventListener('resize', updateChartWidth);
  }, []);
  
  // Process merge fields and format text content
  const htmlContent = formatTextContent(data.content?.text, userProgress);

  // Helper function to calculate aggregated emotional health score
  const calculateEmotionalHealthScore = (emotionalStates) => {
    if (!emotionalStates) return 0;
    
    const factors = ['mania', 'depression', 'energy', 'focus', 'calm', 'happiness', 'hours'];
    const presentFactors = factors.filter(factor => 
      emotionalStates[factor] !== undefined && emotionalStates[factor] !== null
    );
    
    if (presentFactors.length === 0) return 0;
    
    const sum = presentFactors.reduce((total, factor) => total + emotionalStates[factor], 0);
    return sum / presentFactors.length;
  };

  // Helper function to create visualization data points
  const createVisualizationData = (journalEntries) => {
    if (!journalEntries || journalEntries.length === 0) return [];
    
    return journalEntries
      .filter(entry => entry.pressureScore !== undefined && entry.emotionalStates)
      .map(entry => ({
        pressure: entry.pressureScore,
        emotionalHealth: calculateEmotionalHealthScore(entry.emotionalStates),
        timestamp: entry.timestamp
      }))
      .sort((a, b) => a.timestamp - b.timestamp); // Sort by timestamp for chronological order
  };

  // Helper function to calculate linear regression (best fit line)
  const calculateLinearRegression = (dataPoints) => {
    if (dataPoints.length < 2) return null;

    const n = dataPoints.length;
    const sumX = dataPoints.reduce((sum, point) => sum + point.pressure, 0);
    const sumY = dataPoints.reduce((sum, point) => sum + point.emotionalHealth, 0);
    const sumXY = dataPoints.reduce((sum, point) => sum + point.pressure * point.emotionalHealth, 0);
    const sumXX = dataPoints.reduce((sum, point) => sum + point.pressure * point.pressure, 0);

    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    return { slope, intercept };
  };

  // Helper function to render the bar visualization
  const renderBarVisualization = (score) => {
    if (score === undefined || score === null) return null;

    // Define color segments based on breakpoints (darkest to lightest, right to left)
    const segments = [
      { start: 0, end: 35, color: theme.colors.doryBlack, label: '0-35' },
      { start: 35, end: 50, color: theme.colors.doryGreen, label: '35-50' },
      { start: 50, end: 70, color: theme.colors.doryDarkerBlue, label: '50-70' },
      { start: 70, end: 85, color: theme.colors.doryBlue, label: '70-85' },
      { start: 85, end: 100, color: theme.colors.doryLighterBlue, label: '85-100' }
    ];

    // Calculate position of score indicator (0-100 scale)
    const scorePosition = Math.max(0, Math.min(100, score));
    const indicatorLeft = `${scorePosition}%`;

    return (
      <BarContainer>
        <BarWrapper>
          {segments.map((segment, index) => {
            const left = `${segment.start}%`;
            const width = `${segment.end - segment.start}%`;
            const isFirst = index === 0;
            const isLast = index === segments.length - 1;
            
            return (
              <BarSegment
                key={index}
                style={{
                  left,
                  width,
                  backgroundColor: segment.color,
                  borderTopLeftRadius: isFirst ? theme.borderRadius.lg : '0',
                  borderBottomLeftRadius: isFirst ? theme.borderRadius.lg : '0',
                  borderTopRightRadius: isLast ? theme.borderRadius.lg : '0',
                  borderBottomRightRadius: isLast ? theme.borderRadius.lg : '0'
                }}
              />
            );
          })}
          
          <BarLabels>
            <BarLabel>0</BarLabel>
            <BarLabel>100</BarLabel>
          </BarLabels>
          
          <ScoreIndicator style={{ left: indicatorLeft }}>
            <ScoreLabel>{Math.round(score)}</ScoreLabel>
          </ScoreIndicator>
        </BarWrapper>
      </BarContainer>
    );
  };

  // Helper function to render the visualization
  const renderVisualization = (dataPoints) => {
    if (dataPoints.length === 0) return null;

    // Use the state value for chart width
    const chartHeight = 130; // Increased height (30% more than 100px)
    const containerPadding = 10; // Chart container has 10px padding on left and right
    const chartPadding = 10; // Internal chart padding

    // Fixed scales: X-axis 0-10 (pressure), Y-axis 0-10 (emotional health)
    const effectiveWidth = chartWidth - 2 * containerPadding; // Account for container padding
    const effectiveHeight = chartHeight - 2 * chartPadding;
    
    const scaleX = (pressure) => containerPadding + (pressure / 10) * effectiveWidth;
    const scaleY = (emotional) => chartPadding + ((10 - emotional) / 10) * effectiveHeight; // Invert Y so 0 is at bottom, 10 at top

    // Calculate best fit line
    const regression = calculateLinearRegression(dataPoints);

    return (
      <VisualizationContainer>
        <SlopeLabel
          style={{
            position: 'relative',
            left: 'auto',
            top: 'auto',
            transform: 'none',
            margin: '0 auto',
            textAlign: 'center'
          }}
        >
          {(() => {
            const score = mentalResilienceData?.score;
            if (score > 85) return 'Performance Increases Under Pressure';
            if (score == 85) return 'Performance Holds Steady Under Pressure';
            if (score >= 70) return 'Performance Falls Slightly Under Pressure';
            if (score >= 50) return 'Performance Falls Moderately Under Pressure';
            if (score >= 35) return 'Performance Falls Significantly Under Pressure';
            if (score < 35) return 'Performance Crashes Under Pressure';
            return 'Performance Crashes Under Pressure';
          })()}
        </SlopeLabel>
        <ChartContainer ref={chartRef}>
          <ChartArea>
            <XAxis 
              style={{
                left: scaleX(0),
                width: scaleX(10) - scaleX(0)
              }}
            />
            <YAxis 
              style={{
                left: scaleX(0)
              }}
            />
            <XAxisLabel>Pressure</XAxisLabel>
            <YAxisLabel>Performance</YAxisLabel>
            
            {/* Grid lines for X-axis values 0-10 */}
            {Array.from({ length: 11 }, (_, i) => (
              <GridLine
                key={`grid-${i}`}
                style={{
                  left: scaleX(i),
                  top: 0,
                  height: effectiveHeight
                }}
              />
            ))}
            
            {/* Render best fit line if we have regression data */}
            {regression && (() => {
              // Calculate line endpoints - shorten the line slightly
              const x1 = 1;
              const y1 = regression.intercept + regression.slope * 1;
              const x2 = 9; // Shorten the line
              const y2 = regression.intercept + regression.slope * 9;
              
              // Convert to pixel coordinates
              const pixelX1 = scaleX(x1);
              const pixelY1 = scaleY(y1);
              const pixelX2 = scaleX(x2);
              const pixelY2 = scaleY(y2);
              
              // Calculate line length and angle
              const length = Math.sqrt((pixelX2 - pixelX1) ** 2 + (pixelY2 - pixelY1) ** 2);
              const angle = Math.atan2(pixelY2 - pixelY1, pixelX2 - pixelX1) * (180 / Math.PI);
              
              return (
                <>
                  <BestFitLine
                    style={{
                      left: pixelX1,
                      top: pixelY1,
                      width: length,
                      transform: `rotate(${angle}deg)`,
                      transformOrigin: 'left center'
                    }}
                  />
                </>
              );
            })()}
            
            {/* Render data points */}
            {dataPoints.map((point, index) => (
              <DataPoint
                key={`point-${index}`}
                style={{
                  left: scaleX(point.pressure),
                  top: scaleY(point.emotionalHealth)
                }}
                onMouseEnter={() => setHoveredPoint(index)}
                onMouseLeave={() => setHoveredPoint(null)}
              >
                {hoveredPoint === index && (
                  <Tooltip>
                    Pressure: {point.pressure.toFixed(1)}<br />
                    Performance: {point.emotionalHealth.toFixed(1)}
                  </Tooltip>
                )}
              </DataPoint>
            ))}
          </ChartArea>
        </ChartContainer>
      </VisualizationContainer>
    );
  };

  // Check if we have mental resilience data
  if (!mentalResilienceData) {
    return (
      <Container>
        <Content>
          {htmlContent && (
            <Description dangerouslySetInnerHTML={{ __html: htmlContent }} />
          )}
          
          <Title>Your Mental Resilience</Title>
          
          <NoDataMessage>
            <NoDataTitle>No Data Available</NoDataTitle>
            <NoDataText>
              Complete a journal entry to see your mental resilience score.
            </NoDataText>
          </NoDataMessage>
          
          {/* CTA button appears right after content */}
          <ContinueButton onClick={() => onNext?.(data.key)}>
            {data.content?.ctaText || 'Continue'}
          </ContinueButton>
        </Content>
      </Container>
    );
  }

  const { score } = mentalResilienceData;

  // Create visualization data
  const visualizationData = createVisualizationData(userProgress.journalEntries || []);

  return (
    <Container>
      <Content>
        {/* Bar visualization appears at the top */}
        {renderBarVisualization(score)}
        
        {htmlContent && (
          <Description dangerouslySetInnerHTML={{ __html: htmlContent }} />
        )}
        
        {/* Chart visualization appears above the score interpretation */}
        {visualizationData.length > 0 && renderVisualization(visualizationData)}
        
        {/* CTA button appears right after content */}
        <ContinueButton onClick={() => onNext?.(data.key)}>
          {data.content?.ctaText || 'Continue'}
        </ContinueButton>
      </Content>
    </Container>
  );
};

export default MentalResilienceScreen; 